import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {DotsThreeVertical} from '../../icons';

/**
 * Un menú desplegable de Plataformas: el ⋮ de cada acceso y de cada tienda, y «Conectar una
 * plataforma» de arriba.
 *
 * Es el mismo patrón que `LineActionsMenu` del Panel (el ⋮ que la 2.1 ya usa para «más acciones»):
 * el foco entra al primer ítem al abrir, las flechas lo recorren, Escape cierra y devuelve el foco al
 * botón, y elegir un ítem cierra SIEMPRE, también cuando lo que sigue es un modal.
 *
 * Una diferencia, a propósito: allá el panel va `fixed` porque la tabla de líneas es un scrollport
 * que lo recortaba. Acá ningún ancestro recorta (las tarjetas no llevan `overflow`), así que el panel
 * va `absolute` contra su botón y acompaña a la fila sin medir el scroll. Sólo se mide al abrir, para
 * abrir hacia arriba cuando abajo no entra.
 *
 * `items`: `{key, label, icon, onSelect, danger}`, `{key, divider: true}` o `{key, heading}`.
 */
export function ActionsMenu({label, items, triggerContent, triggerClassName = 'apl-kebab', menuClassName = ''}) {
    const [open, setOpen] = useState(false);
    const [up, setUp] = useState(false);
    const [toRight, setToRight] = useState(false);
    const wrapRef = useRef(null);
    const triggerRef = useRef(null);
    const listRef = useRef(null);
    // Sólo Escape devuelve el foco al botón. Un clic afuera ya lo puso donde el usuario quería, y
    // elegir un ítem lo deja en lo que sigue (un modal, el campo de renombrar).
    const returnFocus = useRef(false);

    const close = useCallback((giveFocusBack = false) => {
        returnFocus.current = giveFocusBack;
        setOpen(false);
    }, []);

    useEffect(() => {
        if (!open) return undefined;
        const onDocumentClick = (event) => {
            if (wrapRef.current && wrapRef.current.contains(event.target)) return;
            close(false);
        };
        const onKeyDown = (event) => {
            if (event.key === 'Escape') close(true);
        };
        document.addEventListener('click', onDocumentClick);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('click', onDocumentClick);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open, close]);

    // Abre hacia arriba sólo si abajo no entra y arriba sí; y hacia la derecha del botón sólo si,
    // alineado a su borde derecho, se saldría por la izquierda (el CTA de arriba, que en angosto baja
    // a su renglón contra el margen izquierdo). Se decide una vez por apertura.
    useLayoutEffect(() => {
        if (!open || !listRef.current || !triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        const height = listRef.current.offsetHeight;
        const width = listRef.current.offsetWidth;
        const viewport = document.documentElement.clientHeight;
        setUp(height > 0 && rect.bottom + height + 8 > viewport && rect.top - height - 8 > 0);
        setToRight(width > 0 && rect.right - width < 8);
    }, [open]);

    useEffect(() => {
        if (open) {
            const first = listRef.current && listRef.current.querySelector('[role="menuitem"]');
            if (first) first.focus();
            return;
        }
        if (returnFocus.current) {
            returnFocus.current = false;
            if (triggerRef.current) triggerRef.current.focus();
        }
    }, [open]);

    // Es un `role="menu"`: el patrón ARIA pide que las flechas muevan el foco, no el Tab.
    const handleListKeyDown = (event) => {
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        const nodes = [...listRef.current.querySelectorAll('[role="menuitem"]')];
        if (!nodes.length) return;
        event.preventDefault();
        const current = nodes.indexOf(document.activeElement);
        let next;
        if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = nodes.length - 1;
        else if (event.key === 'ArrowDown') next = (current + 1) % nodes.length;
        else next = (current <= 0 ? nodes.length : current) - 1;
        nodes[next].focus();
    };

    // Si el foco se va del menú con Tab, se cierra: un panel abierto que ya no tiene el foco queda
    // tapando la fila de abajo.
    const handleBlur = (event) => {
        if (!open) return;
        if (wrapRef.current && event.relatedTarget && wrapRef.current.contains(event.relatedTarget)) return;
        if (event.relatedTarget) close(false);
    };

    const run = (item) => () => {
        close(false);
        item.onSelect();
    };

    return (
        <span className="apl-menu" ref={wrapRef} onBlur={handleBlur}>
            <button type="button" ref={triggerRef} className={triggerClassName}
                    aria-haspopup="menu" aria-expanded={open}
                    aria-label={triggerContent ? undefined : label}
                    onClick={() => setOpen((value) => !value)}>
                {triggerContent || <DotsThreeVertical size={18} weight="bold" aria-hidden="true"/>}
            </button>
            {open && (
                <span className={`apl-menu__list${up ? ' -up' : ''}${toRight ? ' -to-right' : ''}${menuClassName ? ` ${menuClassName}` : ''}`}
                      role="menu" aria-label={label} ref={listRef} onKeyDown={handleListKeyDown}>
                    {items.map((item) => {
                        if (item.divider) return <span key={item.key} className="apl-menu__divider" role="separator"/>;
                        if (item.heading) {
                            return <span key={item.key} className="apl-menu__heading" role="presentation">{item.heading}</span>;
                        }
                        return (
                            <button key={item.key} type="button" role="menuitem"
                                    className={`apl-menu__item${item.danger ? ' -danger' : ''}`}
                                    onClick={run(item)}>
                                {item.icon}
                                <span>{item.label}</span>
                            </button>
                        );
                    })}
                </span>
            )}
        </span>
    );
}

export default ActionsMenu;
